import { ProductImpressionTrackingInfo } from "@omni/types/components/tracking/tracking-info/impression/productImpression.trackingInfo";
import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { debounce } from "@omni/utils/function.utils";

import FeatureTogglesService from "@/services/featureToggle/featureToggles.service";
import { EnrichedAnalyticsEvent } from "@/utils/analytics/analytics.types";

import { GoogleAnalyticsEvents } from "../GoogleAnalyticsEvents";
import {
    GoogleAnalyticsMinimumDataset,
    getPageInfo,
    getUserInfo
} from "../GoogleAnalyticsMinimumDataset";
import {
    GoogleAnalyticsProduct,
    getGoogleAnalyticsProduct
} from "./GoogleAnalyticsProduct";

export interface GoogleAnalyticsProductImpression
    extends GoogleAnalyticsMinimumDataset {
    ecommerce: {
        currencyCode: string;
        impressions: GoogleAnalyticsProduct[];
    };
}

const listQueue: Record<string, Array<GoogleAnalyticsProduct>> = {};

const debounceHandleEvents = debounce(
    (listName: string, data: GoogleAnalyticsProductImpression) => {
        const queue = listQueue[listName];
        if (queue.length === 0) {
            return;
        }
        const gtmData = {
            ...data,
            ecommerce: {
                ...data.ecommerce,
                impressions: [queue]
            }
        };

        window.dataLayer.push(gtmData);
        listQueue[listName] = [];

        if (
            FeatureTogglesService.isEnabled(
                FeatureToggleKey.DEBUG_ANALYTICS_LOGGING
            )
        ) {
            console.groupCollapsed(
                `hubo:google-analytics - event`,
                gtmData.event
            );
            console.log(gtmData);
            console.groupEnd();
        }
    },
    300
);

const handleGoogleAnalyticsProductImpression = (
    data: EnrichedAnalyticsEvent<ProductImpressionTrackingInfo>
): GoogleAnalyticsProductImpression | undefined => {
    const listName = data.payload.eventData.list;
    if (listName) {
        if (!listQueue[listName]) listQueue[listName] = [];

        const currentList = listQueue[listName];
        currentList.push(getGoogleAnalyticsProduct(data.payload.eventData));
        debounceHandleEvents(listName, {
            event: GoogleAnalyticsEvents.ProductImpression,
            pageInfo: getPageInfo(data),
            userInfo: getUserInfo(data),
            ecommerce: {
                currencyCode: data.payload.eventData.currencyCode
            }
        });
        return undefined;
    }

    return {
        event: GoogleAnalyticsEvents.ProductImpression,
        pageInfo: getPageInfo(data),
        userInfo: getUserInfo(data),
        ecommerce: {
            currencyCode: data.payload.eventData.currencyCode,
            impressions: [getGoogleAnalyticsProduct(data.payload.eventData)]
        }
    };
};

export { handleGoogleAnalyticsProductImpression };
